/**
 * The candle history the chart draws, per symbol and timeframe.
 *
 * `GET /index/{symbol}/candles` is fetched once per (symbol, timeframe) and then
 * *not* polled — the forming bar is extended by `index.{symbol}` on the socket,
 * which is the whole point of invariant 8. Switching timeframe is a user action
 * and therefore a request; sitting on a chart is not.
 *
 * **This replaces the sample buffer the line chart used.** Accumulating 5 Hz
 * ticks into an array only ever produced the last ninety seconds; a candlestick
 * chart a trader can work from needs real history at real intervals, which is a
 * request to make and not a buffer to grow.
 *
 * The response is cached per key, so flicking 1m → 5m → 1m costs one request
 * rather than three. `/index/{symbol}/candles` carries `max-age=1`, so the HTTP
 * layer would revalidate anyway — this avoids even asking.
 *
 * **But only while the entry is still adjacent to now, and that qualifier is
 * the whole of a real defect.** The cache used to be for the life of the tab,
 * on the reasoning that "the bar the cache would refresh is the one the socket
 * is already extending". That is true of exactly one key: the one on screen.
 * Nothing extends the bars of a timeframe you are not looking at, or of the
 * asset you switched away from — so an entry goes stale by precisely the time
 * you spend elsewhere, and coming back re-ran `series.setData` with it, which
 * also *discards* every live bar accumulated since. The series then had a hole
 * in it exactly as wide as the detour, and `<MarketChart>` places the market's
 * window by counting bars: measured `len 303, span 326, gaps 1, biggest 25`,
 * and a window that should have been sixty bars wide landing off the end of
 * the data. That is the "random x-axis range" a user sees after switching
 * asset or timeframe and back.
 *
 * A stale entry is still rendered immediately and refetched behind it, so the
 * pane never blanks — it is the seed, not the answer.
 *
 * **There is a second detour and it is not a key, it is a hidden tab.** The
 * paragraph above says "nothing extends the bars of a timeframe you are not
 * looking at"; the sharper statement is that nothing extends the bars of a
 * *renderer that is not running*. `index.{symbol}` coalesces `latest`
 * (`api/ws/channels/index.ts`), the flush that drains it is
 * `requestAnimationFrame` (`lib/raf.ts`), and Chrome does not run rAF in a
 * background tab — so an alt-tab discards every index frame but the last, and
 * the return appends exactly one bar, at the second it happened. Every second
 * in between has no bar at all.
 *
 * The result is the same hole and the same symptom, reached from the other
 * side: bars bunch up (the chart spaces them by index, not by time), the time
 * axis goes irregular — `09:26 → 09:26:06 → 09:26:17`, six seconds and eleven
 * seconds drawn the same width — and the pinned market window is placed
 * against a series that is no longer a dense grid. A reload fixed it because a
 * reload refetches this endpoint, which is the repair spelled out.
 *
 * So `reachesNow` is re-evaluated on `visibilitychange → visible`, and that is
 * the whole fix: the predicate already asks the right question, it was simply
 * never asked again after mount. It scales with the interval for free — a
 * thirty-second absence loses nothing at `1h` and a whole minute of bars at
 * `1s`, and the two intervals of slack say so without a second rule.
 *
 * **What it does not cover**, deliberately and worth knowing before someone
 * measures it: a window merely *occluded* by another one keeps
 * `visibilityState === 'visible'` while Chrome throttles its rAF anyway. That
 * gaps the series with no event to hang a refetch on. `docs/03` §9.2 has no
 * row for it and neither does this.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRuntime } from './use-runtime'
import { HISTORY_BARS, INDEX_INTERVAL, type ChartCandle, type Timeframe } from '@/components/market'

export interface CandleHistory {
  candles: readonly ChartCandle[]
  loading: boolean
  error: unknown
}

const EMPTY: readonly ChartCandle[] = []

/** `openapi.IndexInterval` in milliseconds — what "still adjacent to now"
 *  means for each key. `INDEX_INTERVAL` maps a timeframe onto one of these. */
export const INTERVAL_MS: Record<string, number> = {
  '1s': 1_000,
  '5s': 5_000,
  '15s': 15_000,
  '1m': 60_000,
  '5m': 300_000,
  '15m': 900_000,
  '1h': 3_600_000,
  '4h': 14_400_000,
  '1d': 86_400_000,
}

/**
 * Can the socket continue from these bars without leaving a hole?
 *
 * **Adjacency, not age.** The question is never how long ago the response was
 * fetched — it is whether the newest bar is the bucket the clock is in, or the
 * one before it. Two intervals of slack absorbs a bucket that has not closed
 * yet plus a response that was briefly in flight; past that, the gap is a
 * detour the socket did not cover for this key, because nothing extends the
 * bars of a timeframe you are not looking at or an asset you switched away
 * from.
 *
 * Pure and exported so the rule can be tested without a runtime: the effect
 * around it is a cache and a fetch, and this is the decision.
 */
export function reachesNow(
  candles: readonly ChartCandle[],
  interval: string,
  nowMs: number,
): boolean {
  const newest = candles[candles.length - 1]?.open_time_ms
  if (typeof newest !== 'number') return false
  return nowMs - newest <= 2 * (INTERVAL_MS[interval] ?? 1_000)
}

/**
 * Could an absence of `hiddenMs` have cost the series a bar?
 *
 * A pre-filter on `reachesNow`, not a second opinion — the two never disagree,
 * because a cache entry stops reaching now at almost exactly the point a bucket
 * starts being missable. It exists so a 200ms flick to another tab and back is
 * free: at `1s`, `reachesNow` on a cache entry fetched more than two seconds
 * ago is false *whatever* happened while hidden, so without this every tab
 * switch on the trade screen would fetch three hundred bars to replace three
 * hundred identical ones.
 *
 * One bucket, not two. `reachesNow`'s slack absorbs a bucket that has not
 * closed yet; this is asking whether one could have closed *unobserved*, and
 * one is enough to put a hole in the grid.
 *
 * Pure and exported for the reason `reachesNow` is: the effect around it owns a
 * listener and a fetch, and this is the decision.
 */
export function mayHaveGapped(hiddenMs: number, interval: string): boolean {
  return hiddenMs >= (INTERVAL_MS[interval] ?? 1_000)
}

export function useIndexCandles(symbol: string, timeframe: Timeframe): CandleHistory {
  const runtime = useRuntime()
  const cache = useRef<Map<string, readonly ChartCandle[]>>(null)
  cache.current ??= new Map()

  // **The interval, not the timeframe**, and it is both halves of one rule.
  // `market` is a viewport over `1s` rather than an interval of its own
  // (`MarketChart`'s `INDEX_INTERVAL`), so sending it would be an
  // `INVALID_PARAMETER` — and keying the cache on it would fetch a second copy
  // of bars the client already holds. Resolved once, used for both.
  const interval = INDEX_INTERVAL[timeframe]
  const key = `${symbol}:${interval}`
  const [state, setState] = useState<{ key: string; candles: readonly ChartCandle[]; loading: boolean; error: unknown }>(
    () => ({ key, candles: EMPTY, loading: true, error: null }),
  )

  // A counter rather than a boolean, and it is only ever a dependency: two
  // absences long enough to gap must ask twice, and a flag that was already
  // `true` from the first would answer the second from cache — which is the
  // defect this hook exists for, one level up.
  const [wake, setWake] = useState(0)

  useEffect(() => {
    const doc = globalThis.document
    if (!doc) return
    // Held here rather than in a ref: it is this listener's own state, it means
    // nothing to a render, and a ref would keep a stale instant alive across a
    // symbol change.
    let hiddenAtMs: number | null = null

    const onVisibility = () => {
      if (doc.hidden) {
        hiddenAtMs = runtime.clock.now()
        return
      }
      const since = hiddenAtMs
      hiddenAtMs = null
      // Visible without having been hidden — the initial event on some paths,
      // and a duration measured from `null` would be the age of the epoch.
      if (since === null) return
      if (!mayHaveGapped(runtime.clock.now() - since, interval)) return
      setWake((n) => n + 1)
    }

    doc.addEventListener('visibilitychange', onVisibility)
    return () => doc.removeEventListener('visibilitychange', onVisibility)
  }, [runtime, interval])

  useEffect(() => {
    const held = cache.current?.get(key)
    if (held && reachesNow(held, interval, runtime.clock.now())) {
      setState({ key, candles: held, loading: false, error: null })
      return
    }

    // Aborted on a fast timeframe flick, so a slow 1h response cannot land
    // after the 1m the user actually settled on.
    const controller = new AbortController()
    // A stale entry still renders while its replacement is in flight. Blanking
    // the pane to refetch bars that are only seconds behind would be a worse
    // answer than the slightly old ones.
    setState({ key, candles: held ?? EMPTY, loading: true, error: null })

    runtime.api.index
      .getIndexCandles(
        symbol as 'BTC-USD' | 'ETH-USD',
        { interval, limit: HISTORY_BARS },
        { signal: controller.signal },
      )
      .then((page) => {
        const candles = ((page as { data?: ChartCandle[] }).data ?? []) as readonly ChartCandle[]
        cache.current?.set(key, candles)
        setState({ key, candles, loading: false, error: null })
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        // The chart keeps whatever it has and says nothing new. A failed
        // history fetch is not a reason to blank a pane that is still being
        // extended by the socket — including the stale entry this call was
        // sent to replace, which is old but is not nothing.
        setState({ key, candles: cache.current?.get(key) ?? EMPTY, loading: false, error })
      })

    return () => controller.abort()
    // `wake` is a dependency and nothing reads it. It is how a tab coming back
    // re-asks `reachesNow` above — the effect body is already the right
    // decision, it was only ever run on mount and on a key change.
  }, [runtime, symbol, interval, key, wake])

  return useMemo(
    () =>
      state.key === key
        ? { candles: state.candles, loading: state.loading, error: state.error }
        : { candles: EMPTY, loading: true, error: null },
    [state, key],
  )
}
